/*
 * Purpose: Reactive auth + tenant state for UI
 * Last modified: 2026-07-10 | Agent
 * Completeness: 94
 */
import { create } from 'zustand';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { APP_ROUTES } from '@/lib/appRoutes';
import {
  getActiveTenant,
  getSessionEmail,
  getSessionTenants,
  isAuthenticated,
  isAuthenticatedAsync,
  needsTenantSelection,
  setActiveTenantAsync,
  signIn as sessionSignIn,
  signOutUnified,
  type SignInOptions,
} from '@/lib/auth-session';
import { fetchUserState } from '@/lib/user-state-api';
import { mergeSubscriptionState } from '@/lib/subscriptionState';
import {
  needsPlanSelection,
  subscriptionFromState,
  type SubscriptionAccess,
} from '@/lib/subscriptionPolicy';
import type { AuthTenant } from '@/lib/auth-tenants';

interface AuthState {
  ready: boolean;
  authed: boolean;
  email: string | null;
  tenants: AuthTenant[];
  activeTenant: AuthTenant | null;
  tenantPickRequired: boolean;
  planPickRequired: boolean;
  subscription: SubscriptionAccess;
  serverUserState: Record<string, unknown> | null;
  hydrate: () => Promise<void>;
  signIn: (email: string, options?: SignInOptions) => void;
  selectTenant: (tenantId: string) => Promise<void>;
  refreshSubscription: (state?: Record<string, unknown>) => Promise<void>;
  signOut: () => Promise<void>;
}

function derivePlanPickRequired(
  authed: boolean,
  tenantPickRequired: boolean,
  serverUserState: Record<string, unknown> | null
): boolean {
  if (!authed || tenantPickRequired) return false;
  return needsPlanSelection(subscriptionFromState(serverUserState));
}

export const useAuthStore = create<AuthState>((set) => ({
  ready: false,
  authed: false,
  email: null,
  tenants: [],
  activeTenant: null,
  tenantPickRequired: false,
  planPickRequired: false,
  subscription: subscriptionFromState(null),
  serverUserState: null,

  hydrate: async () => {
    const authed = AUTH_CONFIG.mock ? isAuthenticated() : await isAuthenticatedAsync();
    let serverUserState: Record<string, unknown> | null = null;
    if (authed && !AUTH_CONFIG.mock && getActiveTenant()) {
      serverUserState = mergeSubscriptionState(
        (await fetchUserState()) as Record<string, unknown> | null
      );
    } else if (authed && AUTH_CONFIG.mock) {
      serverUserState = mergeSubscriptionState(null);
    }
    const tenantPickRequired = needsTenantSelection();
    const subscription = subscriptionFromState(serverUserState);
    set({
      ready: true,
      authed,
      email: getSessionEmail(),
      tenants: getSessionTenants(),
      activeTenant: getActiveTenant(),
      tenantPickRequired,
      planPickRequired: derivePlanPickRequired(authed, tenantPickRequired, serverUserState),
      subscription,
      serverUserState,
    });
  },

  signIn: (email, options) => {
    sessionSignIn(email, options);
    const serverUserState = mergeSubscriptionState(null);
    const tenantPickRequired = needsTenantSelection();
    set({
      authed: true,
      email: getSessionEmail(),
      tenants: getSessionTenants(),
      activeTenant: getActiveTenant(),
      tenantPickRequired,
      planPickRequired: derivePlanPickRequired(true, tenantPickRequired, serverUserState),
      subscription: subscriptionFromState(serverUserState),
      serverUserState,
    });
  },

  selectTenant: async (tenantId) => {
    await setActiveTenantAsync(tenantId);
    const serverUserState = AUTH_CONFIG.mock
      ? mergeSubscriptionState(null)
      : mergeSubscriptionState((await fetchUserState()) as Record<string, unknown> | null);
    const subscription = subscriptionFromState(serverUserState);
    set({
      activeTenant: getActiveTenant(),
      tenantPickRequired: false,
      planPickRequired: derivePlanPickRequired(true, false, serverUserState),
      subscription,
      serverUserState,
    });
  },

  refreshSubscription: async (state) => {
    const merged = mergeSubscriptionState(state ?? useAuthStore.getState().serverUserState);
    const subscription = subscriptionFromState(merged);
    set({
      serverUserState: merged,
      subscription,
      planPickRequired: derivePlanPickRequired(
        useAuthStore.getState().authed,
        useAuthStore.getState().tenantPickRequired,
        merged
      ),
    });
  },

  signOut: async () => {
    await signOutUnified();
    set({
      authed: false,
      email: null,
      tenants: [],
      activeTenant: null,
      tenantPickRequired: false,
      planPickRequired: false,
      subscription: subscriptionFromState(null),
      serverUserState: null,
    });
    if (typeof window !== 'undefined') {
      window.location.replace(APP_ROUTES.home);
    }
  },
}));
